import { useState } from "react";
import { DrawerForm } from "../../../../components/drawers/DrawerForm";
import { useHub } from "../../../../core/Global";
import { toastError } from "../../../../components/base/Toast";
import PaymentMethodPicker, { NEW_CARD } from "./PaymentMethodPicker";
import FlexRow from "../../../../components/base/FlexRow";
import Icon from "../../../../components/base/Icon";
import AddonCatalog from "../models/AddonCatalog";
import "./AddonsCard.css";


const moneyFormatter = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" })


// Buying extra concurrent users: pick how many, pick a card, pay.
//
// Its own drawer rather than the one every other add-on uses, because this is the only add-on
// bought by the number -- and the only one that can be bought again while some are already
// running. Each purchase is new, with its own renewal date; the account gets the sum of them.
export default function ExtraUsersDrawer(props: {
    capability: AddonCatalog,
    // How many to start with, when the page already knows -- a downgrade that is short of room.
    initialCount?: number,
    onClose: () => void,
    onPaid: () => void
}) {

    const hub = useHub()

    const [count, countSet] = useState<number>(Math.min(Math.max(props.initialCount ?? 1, 1), 10))
    const [methodId, methodIdSet] = useState<string>()

    // Nothing can be paid for until there is something to pay with.
    const [loadingMethods, loadingMethodsSet] = useState<boolean>(true)

    const cap = props.capability
    const max = cap.maxQuantity && cap.maxQuantity > 0 ? cap.maxQuantity : 10
    const unit = cap.price ?? 0
    const total = moneyFormatter.format(unit * count)
    const owned = cap.quantity ?? 0

    const buy = (callback: any) => {

        // A new card goes through Stripe Checkout and comes back to this page; the users are
        // switched on from what the session says was bought.
        if (methodId === NEW_CARD) {
            hub.Post("/accounts/profile/plan/extrausers/newcard", { count }, res => {
                if (res.success && res.data?.url) {
                    document.location.href = res.data.url
                    return
                }
                toastError(res.message ?? "Couldn't start the payment.")
                callback(res)
            })
            return
        }

        hub.Post("/accounts/profile/plan/extrausers/buy", { count, paymentMethodId: methodId }, res => {
            if (res.success) props.onPaid()
            else toastError(res.message)
            callback(res)
        })
    }

    return (
        <DrawerForm id="drawer-extra-users"
            title="Add Extra Users"
            buttonText={"Pay " + total}
            confirmDisabled={loadingMethods || !methodId}
            onClose={props.onClose}
            onSubmit={(body, callback) => buy(callback)}>

            <div>
                <FlexRow className="mb-8">
                    <div className="plan-addon-icon me-4">
                        <Icon icon="user-plus" type="solid" size={28} />
                    </div>
                    <div>
                        <div className="fw-bold fs-4 mb-1">Extra Users</div>
                        <div className="fs-7 text-gray-600">{moneyFormatter.format(unit)} per user / month</div>
                    </div>
                </FlexRow>

                <p className="fs-6 mb-6">
                    Each extra user lets one more person be signed in at the same time. They are
                    added the moment the payment goes through and renew every month from today.
                    {
                        owned > 0 &&
                        <> You already have <b>{owned}</b> extra {owned === 1 ? "user" : "users"}; these
                            are added on top.</>
                    }
                </p>

                <div className="fw-bold mb-3">How many users?</div>
                <div className="extra-users-picker mb-3">
                    {
                        Array.from({ length: max }, (_, i) => i + 1).map(n =>
                            <button key={n} type="button"
                                className={"extra-users-option" + (n === count ? " is-selected" : "")}
                                onClick={e => { e.preventDefault(); countSet(n); }}>
                                {n}
                            </button>
                        )
                    }
                </div>
                <div className="extra-users-total mb-8">
                    <span>{count} × {moneyFormatter.format(unit)}</span>
                    <span className="fw-bolder">{total} / month</span>
                </div>

                <div className="fw-bold mb-3">Payment Method</div>
                <PaymentMethodPicker selectedMethodId={methodId} onChange={methodIdSet}
                    onLoadingChange={loadingMethodsSet} />
            </div>

        </DrawerForm>
    )
}
